import { create } from 'zustand';
import {
  BlockedUser,
  BlockedUserOwnerType,
  UserRole,
} from '../generated/gql-types';
import { blockedUserActions } from './blockedUser.action';

interface BlockedUserState {
  blockedUsers: BlockedUser[];
  isLoading: boolean;
  error: string | null;
  fetchBlockedUsersForOwner: (
    ownerId: string,
    ownerType: BlockedUserOwnerType,
    blockedUserRole?: UserRole,
  ) => Promise<void>;
  blockUser: (
    ownerId: string,
    ownerType: BlockedUserOwnerType,
    userId: string,
    reason?: string,
  ) => Promise<void>;
  unblockUser: (
    ownerId: string,
    ownerType: BlockedUserOwnerType,
    userId: string,
  ) => Promise<void>;
  isUserBlockedForOwner: (ownerId: string, userId: string) => boolean;
  getBlockedUsersForOwner: (ownerId: string) => BlockedUser[];
}

export const useBlockedUserStore = create<BlockedUserState>()((set, get) => ({
  blockedUsers: [],
  isLoading: false,
  error: null,
  fetchBlockedUsersForOwner: async (
    ownerId: string,
    ownerType: BlockedUserOwnerType,
    blockedUserRole?: UserRole,
  ) => {
    try {
      set({ isLoading: true });
      const fetchedBlockedUsers =
        await blockedUserActions.getBlockedUsersForOwner(
          ownerId,
          ownerType,
          blockedUserRole,
        );

      const otherBlockedUsers = get().blockedUsers.filter(
        (blockedUser) => blockedUser.ownerId !== ownerId,
      );
      set({
        blockedUsers: [...otherBlockedUsers, ...fetchedBlockedUsers],
        isLoading: false,
      });
    } catch (error) {
      console.error('Error fetching blocked users:', error);
      set({ error: 'Failed to fetch blocked users', isLoading: false });
      throw error;
    }
  },
  blockUser: async (
    ownerId: string,
    ownerType: BlockedUserOwnerType,
    userId: string,
    reason?: string,
  ) => {
    try {
      set({ isLoading: true });
      const newBlockedUser = await blockedUserActions.blockUser(
        ownerId,
        ownerType,
        userId,
        reason,
      );
      if (newBlockedUser) {
        set({
          blockedUsers: [...get().blockedUsers, newBlockedUser],
          isLoading: false,
        });
      } else {
        set({ error: 'Failed to block user', isLoading: false });
      }
    } catch (error) {
      console.error('Error blocking user:', error);
      set({ error: 'Failed to block user', isLoading: false });
      throw error;
    }
  },
  unblockUser: async (
    ownerId: string,
    ownerType: BlockedUserOwnerType,
    userId: string,
  ) => {
    try {
      set({ isLoading: true });
      const success = await blockedUserActions.unblockUser(
        ownerId,
        ownerType,
        userId,
      );
      if (success) {
        set({
          blockedUsers: get().blockedUsers.filter(
            (blockedUser) =>
              !(
                blockedUser.ownerId === ownerId && blockedUser.userId === userId
              ),
          ),
          isLoading: false,
        });
      } else {
        set({ error: 'Failed to unblock user', isLoading: false });
      }
    } catch (error) {
      console.error('Error unblocking user:', error);
      set({ error: 'Failed to unblock user', isLoading: false });
      throw error;
    }
  },
  isUserBlockedForOwner: (ownerId: string, userId: string) => {
    return get().blockedUsers.some(
      (blockedUser) =>
        blockedUser.ownerId === ownerId && blockedUser.userId === userId,
    );
  },
  getBlockedUsersForOwner: (ownerId: string) => {
    return get().blockedUsers.filter(
      (blockedUser) => blockedUser.ownerId === ownerId,
    );
  },
}));
